import React from 'react'
import { Link } from 'react-router-dom'
import './apiDocs.css'

const ApiDocs = () => {
  return (
    <div className="bodySec aboutSec">
      <div className="container apiDocs">
        <h2 className="text-left">
          <span>Rest API</span>
        </h2>

        <p className="text-justify apiDocs__lead">
          The DeepTrading.ai Rest API lets you access a small set of endpoints using a secure per-user API Key.
          It is designed for individual customers who want to integrate DeepTrading.ai into their own workflows.
        </p>

        <div className="apiDocs__note">
          <div>
            To get API access, purchase the API add-on from <Link to="/pricing-plans">Pricing Plans</Link>.
          </div>
          <ul>
            <li>Base URL: <span className="apiDocs__mono">https://api.deeptrading.ai</span></li>
            <li>Versioned base path: <span className="apiDocs__mono">/api/v1</span> (for example: <span className="apiDocs__mono">https://api.deeptrading.ai/api/v1/balance</span>)</li>
            <li><span className="apiDocs__mono">/v1</span> without <span className="apiDocs__mono">/api</span> is not supported on the public API domain</li>
            <li>Send your API key in: <span className="apiDocs__mono">x-api-key</span> header</li>
            <li>All date windows are business-day aligned (trading days), not calendar days</li>
            <li>The website&apos;s local session API (<span className="apiDocs__mono">/api/*</span>) is separate from this public add-on API</li>
          </ul>
        </div>

        <h3 className="apiDocs__sectionTitle">Authentication</h3>
        <p className="text-justify marginParagraph">
          Include your API key as a request header:
        </p>
        <pre className="apiDocs__code">{`x-api-key: YOUR_API_KEY`}</pre>

        <h3 className="apiDocs__sectionTitle">Endpoints</h3>
        <div className="apiDocs__tableWrap">
          <table className="apiDocs__table">
            <thead>
              <tr>
                <th style={{width: 120}}>Method</th>
                <th>Path</th>
                <th style={{width: 360}}>Description</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="apiDocs__mono">GET</td>
                <td className="apiDocs__mono">/api/v1/balance</td>
                <td>
                  Returns your credit balance and the prediction horizons available for your plan.
                </td>
              </tr>
              <tr>
                <td className="apiDocs__mono">GET</td>
                <td className="apiDocs__mono">/api/v1/tickers</td>
                <td>
                  Returns the tracked stock list (tickers + names).
                </td>
              </tr>
              <tr>
                <td className="apiDocs__mono">GET</td>
                <td className="apiDocs__mono">/api/v1/predictions</td>
                <td>
                  Fetches the latest prediction for a single ticker and horizon (includes predicted return / gain-loss). Use
                  <span className="apiDocs__mono"> ticker</span>, <span className="apiDocs__mono">horizon</span>, optional
                  <span className="apiDocs__mono"> run_date</span>, and optional <span className="apiDocs__mono">align=true</span>.
                </td>
              </tr>
              <tr>
                <td className="apiDocs__mono">GET</td>
                <td className="apiDocs__mono">/api/v1/top50</td>
                <td>
                  Returns Top 50 predicted movers for a horizon on a run date.
                </td>
              </tr>
              <tr>
                <td className="apiDocs__mono">GET</td>
                <td className="apiDocs__mono">/api/v1/market-analysis/brief</td>
                <td>
                  Returns Market Analysis data. Supports ticker list, single ticker detail, and general market brief via
                  <span className="apiDocs__mono"> ticker</span>, <span className="apiDocs__mono"> mode=tickers|general|all</span>,
                  and <span className="apiDocs__mono">general=true</span>.
                </td>
              </tr>
              <tr>
                <td className="apiDocs__mono">POST</td>
                <td className="apiDocs__mono">/api/v1/strategies/backtest</td>
                <td>
                  Runs a simple rule-based strategy backtest on a single ticker for a date window. Uses credits.
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <h3 className="apiDocs__sectionTitle">Quick Start (curl)</h3>
        <p className="text-justify marginParagraph">
          1) Check your balance:
        </p>
        <pre className="apiDocs__code">{`curl -s \\
  -H "x-api-key: YOUR_API_KEY" \\
  https://api.deeptrading.ai/api/v1/balance`}</pre>

        <p className="text-justify marginParagraph">
          2) Run a backtest:
        </p>
        <pre className="apiDocs__code">{`curl -s \\
  -X POST \\
  -H "Content-Type: application/json" \\
  -H "x-api-key: YOUR_API_KEY" \\
  -d '{"ticker":"AAPL","horizon":"daily","from_date":"2025-12-01","to_date":"2026-01-15","trade_frequency_bdays":1,"up_action":"buy","down_action":"hold","min_predicted_return":null,"max_predicted_return":null,"min_abs_signal":0.0,"initial_capital":10000}' \\
  https://api.deeptrading.ai/api/v1/strategies/backtest`}</pre>

        <p className="text-justify marginParagraph">
          3) Fetch a single ticker prediction (example: daily):
        </p>
        <pre className="apiDocs__code">{`curl -s \\
  -H "x-api-key: YOUR_API_KEY" \\
  "https://api.deeptrading.ai/api/v1/predictions?ticker=AAPL&horizon=daily"`}</pre>

        <p className="text-justify marginParagraph">
          4) Get Top 50 predicted movers (example: daily):
        </p>
        <pre className="apiDocs__code">{`curl -s \\
  -H "x-api-key: YOUR_API_KEY" \\
  "https://api.deeptrading.ai/api/v1/top50?horizon=daily"`}</pre>

        <p className="text-justify marginParagraph">
          5) Market Analysis briefs (subject + sentiment):
        </p>
        <pre className="apiDocs__code">{`curl -s \\
  -H "x-api-key: YOUR_API_KEY" \\
  "https://api.deeptrading.ai/api/v1/market-analysis/brief"`}</pre>

        <p className="text-justify marginParagraph">
          6) General Market Brief only:
        </p>
        <pre className="apiDocs__code">{`curl -s \\
  -H "x-api-key: YOUR_API_KEY" \\
  "https://api.deeptrading.ai/api/v1/market-analysis/brief?mode=general"`}</pre>

        <p className="text-justify marginParagraph">
          7) Single ticker Market Brief detail:
        </p>
        <pre className="apiDocs__code">{`curl -s \\
  -H "x-api-key: YOUR_API_KEY" \\
  "https://api.deeptrading.ai/api/v1/market-analysis/brief?ticker=AAPL"`}</pre>

        <p className="text-justify marginParagraph">
          8) Get ticker list and general brief in one call:
        </p>
        <pre className="apiDocs__code">{`curl -s \\
  -H "x-api-key: YOUR_API_KEY" \\
  "https://api.deeptrading.ai/api/v1/market-analysis/brief?mode=all"`}</pre>

        <h3 className="apiDocs__sectionTitle">Backtest Request Fields</h3>
        <p className="text-justify marginParagraph">
          The backtest endpoint accepts a JSON body with these fields (all optional fields can be omitted or set to null):
        </p>
        <div className="apiDocs__tableWrap">
          <table className="apiDocs__table">
            <thead>
              <tr>
                <th style={{width: 240}}>Field</th>
                <th style={{width: 160}}>Type</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="apiDocs__mono">ticker</td>
                <td className="apiDocs__mono">string</td>
                <td>Ticker symbol, for example <span className="apiDocs__mono">AAPL</span>.</td>
              </tr>
              <tr>
                <td className="apiDocs__mono">horizon</td>
                <td className="apiDocs__mono">string</td>
                <td>Prediction horizon to act on (example: <span className="apiDocs__mono">daily</span>, <span className="apiDocs__mono">two_day</span>, <span className="apiDocs__mono">weekly</span>).</td>
              </tr>
              <tr>
                <td className="apiDocs__mono">from_date</td>
                <td className="apiDocs__mono">YYYY-MM-DD</td>
                <td>Start date (business-day aligned).</td>
              </tr>
              <tr>
                <td className="apiDocs__mono">to_date</td>
                <td className="apiDocs__mono">YYYY-MM-DD</td>
                <td>End date (business-day aligned).</td>
              </tr>
              <tr>
                <td className="apiDocs__mono">trade_frequency_bdays</td>
                <td className="apiDocs__mono">int</td>
                <td>Minimum business days between decisions. Examples: 1=daily, 5≈weekly, 21≈monthly.</td>
              </tr>
              <tr>
                <td className="apiDocs__mono">up_action</td>
                <td className="apiDocs__mono">buy|sell|hold</td>
                <td>Action when predicted return is ≥ 0 (example: <span className="apiDocs__mono">buy</span>).</td>
              </tr>
              <tr>
                <td className="apiDocs__mono">down_action</td>
                <td className="apiDocs__mono">buy|sell|hold</td>
                <td>Action when predicted return is &lt; 0 (example: <span className="apiDocs__mono">hold</span>).</td>
              </tr>
              <tr>
                <td className="apiDocs__mono">min_predicted_return</td>
                <td className="apiDocs__mono">number|null</td>
                <td>Optional minimum predicted return filter (example: <span className="apiDocs__mono">-0.10</span> means -10%).</td>
              </tr>
              <tr>
                <td className="apiDocs__mono">max_predicted_return</td>
                <td className="apiDocs__mono">number|null</td>
                <td>Optional maximum predicted return filter (example: <span className="apiDocs__mono">0.02</span> means +2%).</td>
              </tr>
              <tr>
                <td className="apiDocs__mono">min_abs_signal</td>
                <td className="apiDocs__mono">number</td>
                <td>Optional fallback filter when no min/max bounds are set. Uses <span className="apiDocs__mono">abs(predicted_return) ≥ min_abs_signal</span>.</td>
              </tr>
              <tr>
                <td className="apiDocs__mono">initial_capital</td>
                <td className="apiDocs__mono">number</td>
                <td>Starting capital (example: <span className="apiDocs__mono">10000</span>).</td>
              </tr>
            </tbody>
          </table>
        </div>

        <h3 className="apiDocs__sectionTitle">Credits</h3>
        <p className="text-justify marginParagraph">
          Some API calls use credits. The backtest endpoint consumes credits based on the requested window length.
          Each response includes an <span className="apiDocs__mono">api</span> section with credit usage details.
        </p>

        <div className="apiDocs__note">
          <div><strong>Security note:</strong> keep your API key private. If you believe it was exposed, rotate it immediately.</div>
        </div>
      </div>
    </div>
  );
};

export default ApiDocs;
